import React, { useState, useEffect } from 'react';
import {
  Alert,
  Drawer,
  Tabs,
  Descriptions,
  Button,
  Space,
  Table,
  Popconfirm,
  message,
  Tag,
  Typography,
  Select,
  Spin,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  CheckCircleOutlined,
  HistoryOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { SubscriptionService } from '../../services/api/subscriptionService';
import {
  Subscription,
  SubscriptionTransition,
  SubscriptionStateHistory,
  SubscriptionStatus,
} from '../../types/subscription';
import { SubscriptionStatusBadge } from './SubscriptionStatusBadge';
import { AssignSubscriptionDialog } from './AssignSubscriptionDialog';

const { TabPane } = Tabs;
const { Title, Text } = Typography;
const { Option } = Select;

interface SubscriptionDetailProps {
  visible: boolean;
  subscription: Subscription;
  onClose: () => void;
  onUpdate: () => void;
}

export const SubscriptionDetail: React.FC<SubscriptionDetailProps> = ({
  visible,
  subscription,
  onClose,
  onUpdate,
}) => {
  const [loading, setLoading] = useState(false);
  const [validTransitions, setValidTransitions] = useState<SubscriptionTransition[]>([]);
  const [stateHistory, setStateHistory] = useState<SubscriptionStateHistory[]>([]);
  const [isAssignDialogVisible, setIsAssignDialogVisible] = useState(false);

  useEffect(() => {
    if (visible && subscription) {
      loadSubscriptionDetails();
    }
  }, [visible, subscription]);

  const loadSubscriptionDetails = async () => {
    setLoading(true);
    try {
      const [transitions, history] = await Promise.all([
        SubscriptionService.getValidTransitions(subscription.id),
        Promise.resolve([] as SubscriptionStateHistory[]),
      ]);
      setValidTransitions(transitions);
      setStateHistory(history);
    } catch (error) {
      console.error('Error loading subscription details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTransition = async (targetStatus: SubscriptionStatus) => {
    try {
      setLoading(true);
      await SubscriptionService.transitionState(subscription.id, {
        target_state: targetStatus,
      });
      message.success('Subscription state updated successfully');
      onUpdate();
      await loadSubscriptionDetails();
    } catch (error: unknown) {
      const msg =
        error instanceof Error ? error.message : 'Failed to transition subscription state';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleUnassignUser = async (userId: string) => {
    try {
      await SubscriptionService.unassignUser(subscription.id, userId);
      message.success('User unassigned successfully');
      onUpdate();
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Failed to unassign user';
      message.error(msg);
    }
  };

  const userColumns = [
    {
      title: 'Username',
      key: 'username',
      render: (record: any) => record.user?.username || 'N/A',
    },
    {
      title: 'Email',
      key: 'email',
      render: (record: any) => record.user?.email || 'N/A',
    },
    {
      title: 'Assigned At',
      dataIndex: 'assigned_at',
      key: 'assigned_at',
      render: (date: string) => new Date(date).toLocaleString(),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: any) => (
        <Popconfirm
          title="Are you sure you want to unassign this user?"
          onConfirm={() => handleUnassignUser(record.user_id)}
        >
          <Button type="link" danger size="small">
            Unassign
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <>
      <Drawer
        title={
          <Space>
            <Title level={4} style={{ margin: 0 }}>
              {subscription.subscription_type?.name || 'Subscription Details'}
            </Title>
            <SubscriptionStatusBadge status={subscription.status} />
          </Space>
        }
        placement="right"
        width={900}
        onClose={onClose}
        open={visible}
        extra={
          <Space>
            {validTransitions.length > 0 && (
              <Select
                placeholder="Transition State"
                style={{ width: 200 }}
                onChange={(value) => {
                  const transition = validTransitions.find((t) => 
                    (t.to_status || t.to_state_id) === value
                  );
                  if (transition?.to_status) {
                    handleTransition(transition.to_status);
                  }
                }}
                value={null}
              >
                {validTransitions.map((transition) => (
                  <Option 
                    key={transition.id} 
                    value={transition.to_status || transition.to_state_id}
                  >
                    {transition.transition_name}
                  </Option>
                ))}
              </Select>
            )}
            {subscription.status !== 'cancelled' && (
              <Popconfirm
                title="Are you sure you want to cancel this subscription?"
                onConfirm={() => handleTransition('cancelled')}
              >
                <Button danger icon={<DeleteOutlined />}>
                  Cancel Subscription
                </Button>
              </Popconfirm>
            )}
          </Space>
        }
      >
        <Spin spinning={loading}>
          <Tabs defaultActiveKey="overview">
            <TabPane
              tab={
                <span>
                  <SettingOutlined />
                  Overview
                </span>
              }
              key="overview"
            >
              <Descriptions bordered column={2}>
                <Descriptions.Item label="Subscription ID">
                  {subscription.id}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <SubscriptionStatusBadge status={subscription.status} />
                </Descriptions.Item>
                <Descriptions.Item label="Type">
                  {subscription.subscription_type?.name || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Type Code">
                  {subscription.subscription_type?.code || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Created At">
                  {new Date(subscription.created_at).toLocaleString()}
                </Descriptions.Item>
                <Descriptions.Item label="Updated At">
                  {new Date(subscription.updated_at).toLocaleString()}
                </Descriptions.Item>
                {subscription.started_at && (
                  <Descriptions.Item label="Started At">
                    {new Date(subscription.started_at).toLocaleString()}
                  </Descriptions.Item>
                )}
                {subscription.ended_at && (
                  <Descriptions.Item label="Ended At">
                    {new Date(subscription.ended_at).toLocaleString()}
                  </Descriptions.Item>
                )}
                {subscription.artifact_item_id && (
                  <Descriptions.Item label="Artifact Item ID" span={2}>
                    {subscription.artifact_item_id}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </TabPane>

            <TabPane
              tab={
                <span>
                  <UserOutlined />
                  Assigned Users ({subscription.assigned_users?.length || 0})
                </span>
              }
              key="users"
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <Alert
                  type="info"
                  showIcon
                  message="Seats (layer C)"
                  description="Assign users to this company subscription. Create company plans from Company Management → Subscriptions."
                />
                <Button
                  type="primary"
                  icon={<UserOutlined />}
                  onClick={() => setIsAssignDialogVisible(true)}
                >
                  Assign seats
                </Button>
                <Table
                  columns={userColumns}
                  dataSource={subscription.assigned_users || []}
                  rowKey="id"
                  pagination={false}
                  size="small"
                />
              </Space>
            </TabPane>

            <TabPane
              tab={
                <span>
                  <CheckCircleOutlined />
                  Features ({subscription.features?.length || subscription.subscription_type?.features?.length || 0})
                </span>
              }
              key="features"
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                {(subscription.features || subscription.subscription_type?.features || []).map(
                  (feature) => (
                    <Tag key={feature.code} color="blue" style={{ marginBottom: 8 }}>
                      {feature.name} ({feature.code})
                    </Tag>
                  )
                )}
                {(!subscription.features || subscription.features.length === 0) &&
                  (!subscription.subscription_type?.features ||
                    subscription.subscription_type.features.length === 0) && (
                    <Text type="secondary">No features assigned</Text>
                  )}
              </Space>
            </TabPane>

            <TabPane
              tab={
                <span>
                  <HistoryOutlined />
                  State History
                </span>
              }
              key="history"
            >
              {stateHistory.length > 0 ? (
                <Table
                  columns={[
                    {
                      title: 'From Status',
                      dataIndex: 'from_status',
                      key: 'from_status',
                    },
                    {
                      title: 'To Status',
                      dataIndex: 'to_status',
                      key: 'to_status',
                    },
                    {
                      title: 'Transitioned By',
                      dataIndex: 'transitioned_by',
                      key: 'transitioned_by',
                    },
                    {
                      title: 'Reason',
                      dataIndex: 'transition_reason',
                      key: 'transition_reason',
                    },
                    {
                      title: 'Date',
                      dataIndex: 'created_at',
                      key: 'created_at',
                      render: (date: string) => new Date(date).toLocaleString(),
                    },
                  ]}
                  dataSource={stateHistory}
                  rowKey="id"
                  pagination={false}
                  size="small"
                />
              ) : (
                <Text type="secondary">No state history available</Text>
              )}
            </TabPane>
          </Tabs>
        </Spin>
      </Drawer>

      {isAssignDialogVisible && (
        <AssignSubscriptionDialog
          visible={isAssignDialogVisible}
          subscriptionId={subscription.id}
          onCancel={() => setIsAssignDialogVisible(false)}
          onSuccess={() => {
            setIsAssignDialogVisible(false);
            onUpdate();
          }}
        />
      )}
    </>
  );
};

